import React, { useState, useEffect } from "react";
import {
  ActionIcon,
  Button,
  Container,
  Group,
  Menu,
  Radio,
  Stack,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconFilter, IconPackage, IconSearch } from "@tabler/icons-react";
import StockAlertBanner from "./StockAlertBanner";
import ProductsTable from "./ProductsTable";
import ProductDetailModal from "./ProductDetailModal";
import AddProductModal from "./AddProductModal";
import { useProducts } from "../../context/ProductsContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import PageHeader from "../common/PageHeader";

const STATUS_OPTIONS = ["All", "Active", "Inactive", "Archive"];

export default function ProductsPage() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailModalOpened, setDetailModalOpened] = useState(false);
  const [addModalOpened, setAddModalOpened] = useState(false);

  const {
    products,
    isLoading,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    setCurrentPage,
  } = useProducts();
  const { access } = useFeatureAccess();
  const perms = access?.section_permissions?.products;

  useEffect(() => {
    if (selectedProduct) {
      setSelectedProduct(products.find((p) => p.id === selectedProduct.id));
    }
  }, [products]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setDetailModalOpened(true);
  };

  const handleAddProduct = () => {
    setAddModalOpened(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpened(false);
    setSelectedProduct(null);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.currentTarget.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const actions = (
    <Group gap="sm" wrap="nowrap">
      <TextInput
        placeholder="Search products..."
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={handleSearchChange}
        w={260}
        aria-label="Search products"
      />
      <Menu shadow="md" position="bottom-end" closeOnItemClick={false}>
        <Menu.Target>
          <Tooltip label="Filter">
            <ActionIcon
              variant="default"
              size="input-md"
              aria-label="Filter products"
            >
              <IconFilter size={18} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Status</Menu.Label>
          <Stack gap={4} px="sm" py="xs">
            <Radio.Group value={statusFilter} onChange={handleStatusChange}>
              <Stack gap={6}>
                {STATUS_OPTIONS.map((option) => (
                  <Radio key={option} value={option} label={option} />
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        </Menu.Dropdown>
      </Menu>
      {perms?.canEdit !== false && (
        <Button
          onClick={handleAddProduct}
          leftSection={<IconPackage size={16} />}
        >
          Add Product
        </Button>
      )}
    </Group>
  );

  return (
    <Container size="xl" py="md" h="100dvh">
      <BrandedLoadingOverlay visible={isLoading} />
      <Stack gap="lg">
        <PageHeader
          title="Products"
          subtitle="Manage your inventory"
          actions={actions}
        />

        <StockAlertBanner onAlertClick={handleProductClick} />

        <ProductsTable onProductClick={handleProductClick} />

        <ProductDetailModal
          opened={detailModalOpened}
          onClose={handleCloseDetailModal}
          product={selectedProduct}
        />

        <AddProductModal
          opened={addModalOpened}
          onClose={() => setAddModalOpened(false)}
        />
      </Stack>
    </Container>
  );
}
